import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useProfessionalSessionStore } from '@/stores/ProfessionalSessionStore'

/**
 * Route guard for all observer routes.
 * Validates professional session, exchanges auth codes, enforces session status.
 * No hostname check needed - this guard only exists in the observer build.
 */
export async function observerGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  // Allow routes that don't require auth (session-ended, restricted)
  if (!to.meta.requiresProfessionalAuth) {
    return next()
  }

  const sessionStore = useProfessionalSessionStore()

  // If session is active, allow navigation
  if (sessionStore.isProfessionalSession) {
    return next()
  }

  // Dev-only bypass: auto-initialize session without auth code
  if (import.meta.env.DEV) {
    const devSuccess = await sessionStore.initializeFromAuthCode('dev-bypass', 'dev-parent-001')
    if (devSuccess) {
      console.log('[Observer DEV] Auto-initialized session - no auth code needed')
      return next()
    }
  }

  // Check for auth code in URL (one-time code from Portal)
  const code = to.query.code as string | undefined
  const parentId = to.query.parentId as string | undefined

  if (code && parentId) {
    const success = await sessionStore.initializeFromAuthCode(code, parentId)

    if (success) {
      // Strip auth code from URL to prevent replay
      const { code: _code, parentId: _parentId, ...cleanQuery } = to.query
      return next({ ...to, query: cleanQuery, replace: true })
    }

    // Auth code exchange failed
    return next({ name: 'observer-session-ended', query: { reason: 'error' } })
  }

  // Check if session exists but is not active
  if (sessionStore.sessionStatus === 'suspended') {
    return next({ name: 'observer-restricted' })
  }

  if (['revoked', 'expired', 'ended', 'error'].includes(sessionStore.sessionStatus)) {
    return next({
      name: 'observer-session-ended',
      query: { reason: sessionStore.sessionStatus }
    })
  }

  // No session and no auth code - redirect to session-ended
  return next({ name: 'observer-session-ended', query: { reason: 'expired' } })
}
